import {EventDispatcher} from '@megaphonenpm/mputils/src/EventDispatcher.js';
import {isEmpty, shiftFromCollection, union} from '@megaphonenpm/mputils/src/utils.js';
import {log} from '@megaphonenpm/mputils/src/logfunctions.js';
import {CHANGE, COMPLETE, FAIL, TASK_START} from '@megaphonenpm/mputils/src/constants.js'
import {LoadError} from "@megaphonenpm/mputils/src/LoadError.js";
import {MediaAsset} from "./MediaAsset.js";
import {LoadObjectURLTask} from '@megaphonenpm/mputils/src/LoadObjectURLTask.js';

/**
 * A cache of media assets that have been loaded into this application. Use this cache instead of
 * remote URLs to guarantee that the browser will not re-request previously loaded images
 * or videos from the network. To retrieve an image or video from this cache, use get(). The
 * get() method returns a local objectURL, which can be used as an element's src anywhere a
 * public URL could also be used (e.g., `<img src='${mediaCache.get(someImageURL)}'>`).
 */
export class MediaCache extends EventDispatcher {
  // TODO: To prevent long-term memory leaks that would result from loading dozens or hundreds of
  //  playlists with a large number of different images or videos, schedule a period check to scan
  //  the cache and determine whether any cached assets are no longer required (e.g., are not
  //  referenced by the current playlist AND are not currently displayed on screen). The check could
  //  be triggered by load(), or could be triggered periodically (say, once an hour). Release any
  //  unused assets by invoking URL.revokeObjectURL(objectURL). For example:
  //    ususedObjectURL.revokeObjectURL(this.get(unusedObjectURL));
  //  When revoking objectURLs to free memory, be careful not to introduce any rendering delays
  //  (e.g., due to a sudden large garbage collection).
  constructor () {
    super();
    this.cache = new Map();
    this.numFailed = 0;
    this.numSucceeded = 0;
    this.pendingURIs = new Set();
  }

  /**
   * Loads the specified URIs and adds them to the cache as local objectURLs.
   * Assets that fail to load are not added to the cache. When each individual asset's load attempt
   * completes, the MediaCache dispatches a CHANGE event. When ALL load attempts have completed for
   * ALL supplied URIs, the MediaCache dispatches a COMPLETE event.
   *
   * @param uris A list of public URIs for images or videos.
   */
  load (uris) {
    // If no URIs are supplied, quit.
    if (isEmpty(uris)) {
      uris = new Set();
    }

    // Load the assets from the specified URIs...
    if (this.pendingURIs.size > 0) {
      this.pendingURIs = union(this.pendingURIs, uris);
    } else {
      this.pendingURIs = uris;
      this.numFailed = 0;
      this.numSucceeded = 0;
      this.next();
    }
  }

  /**
   * If any requested URIs have not yet been loaded, loads the next one in the queue; otherwise
   * dispatches COMPLETE.
   */
  next () {
    if (this.pendingURIs.size === 0) {
      this.dispatchEvent(COMPLETE);
    } else {
      this.loadNextURI();
    }
  }

  loadNextURI () {
    let uri = shiftFromCollection(this.pendingURIs);
    let loadObjectURLTask;

    if (!this.cache.has(uri)) {
      // This asset is not yet in the cache, so load it.

      loadObjectURLTask = new LoadObjectURLTask(uri);
      this.dispatchEvent(TASK_START, {task: loadObjectURLTask});
      loadObjectURLTask.on(COMPLETE, () => {
        this.handleLoadAttemptCompletion(loadObjectURLTask.url, loadObjectURLTask.objectURL, loadObjectURLTask.type, true)
      });
      loadObjectURLTask.on(FAIL, (e) => {
        let error = e.detail.error;
        if (error instanceof LoadError) {
          log(`Failed to load asset. HTTP status ${error.HTTPStatus}. URI: ${error.uri}`);
        } else {
          log(`Failed to load asset. Internet connection appears to be unavailable.`);
        }
        this.handleLoadAttemptCompletion(uri, null, false);
      });
      loadObjectURLTask.start();
    } else {
      // This asset is already in the cache, so skip it.
      log(`Media asset already cached. Skipping load (${this.pendingURIs.size} remaining):\n  ${uri}`);
      this.next();
    }
  }

  /**
   * Internal method that responds to the completion of a load attempt for a single asset.
   */
  handleLoadAttemptCompletion (uri, objectURL, type, success) {
    if (success) {
      this.cache.set(uri, new MediaAsset(objectURL, type));
      this.numSucceeded++;
    } else {
      this.numFailed++;
    }
    this.dispatchEvent(CHANGE, {uri: uri});
    this.next();
  }

  /**
   * Returns the local objectURL for the cached image or video originally loaded from the specified
   * uri. That objectURL can then be used as the src for any <img> or <video> element in the DOM.
   *
   * See:
   * https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
   *
   * @param uri The URL from which the image or video was originally loaded.
   * @returns A local objectURL corresponding to the original uri.
   */
  get (uri) {
    return this.cache.get(uri);
  }

  get numAssets () {
    return this.numFailed + this.numSucceeded + this.pendingURIs.size;
  }
}
